import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Box, Typography, Paper, Chip, Table, TableHead, TableBody,
    TableRow, TableCell, CircularProgress, Button, IconButton,
    Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, TextField, Grid, Alert,
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { trainerAPI } from '../../services/api';

const statusColors = {
    pending: 'warning',
    accepted: 'success',
    refused: 'error',
};
const statusLabels = {
    pending: 'En attente',
    accepted: 'Acceptée',
    refused: 'Refusée',
};

export default function MyCandidatures() {
    const [candidatures, setCandidatures] = useState([]);
    const [loading, setLoading] = useState(true);

    // Delete dialog state
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Edit dialog state
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadCandidatures();
    }, []);

    const loadCandidatures = async () => {
        try {
            const { data } = await trainerAPI.getCandidatures();
            setCandidatures(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // ── Delete handlers ──────────────────────────────────────
    const handleDeleteClick = (candidature) => {
        setDeleteTarget(candidature);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await trainerAPI.deleteCandidature(deleteTarget.id);
            toast.success('Candidature supprimée avec succès');
            setCandidatures(prev => prev.filter(c => c.id !== deleteTarget.id));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors de la suppression');
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    // ── Edit handlers ────────────────────────────────────────
    const handleEditClick = (candidature) => {
        setEditTarget(candidature);
        setEditForm({
            cin: candidature.cin || '',
            profession: candidature.profession || '',
            diplomes: candidature.diplomes || '',
            specialite_diplome: candidature.specialite_diplome || '',
            experience_professionnelle: candidature.experience_professionnelle || '',
            metier: candidature.metier || '',
        });
    };

    const handleEditChange = (field) => (e) => {
        setEditForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleEditSave = async () => {
        if (!editTarget) return;
        setSaving(true);
        try {
            const { data } = await trainerAPI.updateCandidature(editTarget.id, editForm);
            toast.success('Candidature mise à jour avec succès');
            setCandidatures(prev =>
                prev.map(c => c.id === editTarget.id ? { ...c, ...data } : c)
            );
            setEditTarget(null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Mes candidatures</Typography>
                <Button
                    component={Link} to="/"
                    variant="contained" startIcon={<WorkIcon />}
                    sx={{
                        background: 'linear-gradient(135deg, #1a5276, #2e86c1)',
                        textTransform: 'none', borderRadius: 2,
                    }}
                >
                    Voir les offres
                </Button>
            </Box>

            {candidatures.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                    <WorkIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        Vous n'avez pas encore postulé
                    </Typography>
                    <Button component={Link} to="/" variant="outlined" sx={{ mt: 2, textTransform: 'none' }}>
                        Parcourir les offres
                    </Button>
                </Paper>
            ) : (
                <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <div className="responsive-table-wrapper" style={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Offre</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Filière</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Commentaire</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Documents</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {candidatures.map(c => (
                                    <TableRow key={c.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {c.job_offer?.title || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{c.job_offer?.filieres?.map(f => f.nom).join(', ') || 'N/A'}</TableCell>
                                        <TableCell>
                                            {new Date(c.created_at).toLocaleDateString('fr-FR')}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={statusLabels[c.status] || c.status}
                                                color={statusColors[c.status] || 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {c.director_comment || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={`${c.documents?.length || 0} fichier(s)`} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell align="center">
                                            {c.status === 'pending' ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                    <Tooltip title="Modifier">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleEditClick(c)}
                                                            sx={{
                                                                bgcolor: 'rgba(26,82,118,0.08)',
                                                                '&:hover': { bgcolor: 'rgba(26,82,118,0.16)' },
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Supprimer">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteClick(c)}
                                                            sx={{
                                                                bgcolor: 'rgba(239,68,68,0.08)',
                                                                '&:hover': { bgcolor: 'rgba(239,68,68,0.16)' },
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">—</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Paper>
            )}

            {/* ═══ DELETE CONFIRMATION DIALOG ═══════════════════════ */}
            <Dialog
                open={!!deleteTarget}
                onClose={() => !deleting && setDeleteTarget(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
                    Supprimer la candidature
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir supprimer votre candidature pour l'offre
                        <strong> « {deleteTarget?.job_offer?.title} » </strong> ?
                        Cette action est irréversible et tous les documents associés seront supprimés.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDeleteTarget(null)}
                        disabled={deleting}
                        sx={{ textTransform: 'none' }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        sx={{ textTransform: 'none' }}
                    >
                        {deleting ? 'Suppression...' : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ═══ EDIT DIALOG ═════════════════════════════════════ */}
            <Dialog
                open={!!editTarget}
                onClose={() => !saving && setEditTarget(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    Modifier la candidature
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                        Vous pouvez modifier les informations de votre candidature tant qu'elle est en attente.
                    </Alert>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth label="CIN" value={editForm.cin || ''}
                                onChange={handleEditChange('cin')}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth label="Profession" value={editForm.profession || ''}
                                onChange={handleEditChange('profession')}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth label="Diplômes" value={editForm.diplomes || ''}
                                onChange={handleEditChange('diplomes')}
                                size="small" multiline rows={2}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth label="Spécialité du diplôme" value={editForm.specialite_diplome || ''}
                                onChange={handleEditChange('specialite_diplome')}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth label="Expérience professionnelle" value={editForm.experience_professionnelle || ''}
                                onChange={handleEditChange('experience_professionnelle')}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth label="Métier" value={editForm.metier || ''}
                                onChange={handleEditChange('metier')}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setEditTarget(null)}
                        disabled={saving}
                        sx={{ textTransform: 'none' }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleEditSave}
                        variant="contained"
                        disabled={saving}
                        sx={{
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #1a5276, #2e86c1)',
                        }}
                    >
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
